import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useLocale } from '../../hooks/useLocale';

export interface BasicsInfoDialogRow {
  question: string;
  answer: string;
}

interface BasicsInfoDialogProps {
  open: boolean;
  title: string;
  doneLabel: string;
  rows: BasicsInfoDialogRow[];
  onClose: () => void;
}

export function BasicsInfoDialog({
  open,
  title,
  doneLabel,
  rows,
  onClose,
}: BasicsInfoDialogProps) {
  const { isRTL } = useLocale();

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={overlayStyle}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section style={panelStyle}>
        <header style={headerStyle}>
          <h2 style={titleStyle}>{title}</h2>
          <button type="button" aria-label={doneLabel} onClick={onClose} style={iconButtonStyle}>
            <X size={18} />
          </button>
        </header>
        <div style={bodyStyle}>
          {rows.map((row) => (
            <div key={row.question} style={rowStyle}>
              <h3 style={questionStyle}>{row.question}</h3>
              <p style={answerStyle}>{row.answer}</p>
            </div>
          ))}
        </div>
        <footer style={footerStyle}>
          <button type="button" onClick={onClose} style={doneButtonStyle}>
            {doneLabel}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  backgroundColor: 'rgba(0,0,0,0.4)',
  fontFamily: "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
};

const panelStyle: CSSProperties = {
  width: '100%',
  maxWidth: 440,
  maxHeight: 'min(620px, 86vh)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: 'var(--menu-bg)',
  borderRadius: 28,
  boxShadow: 'var(--gem-dialog-shadow)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '20px 22px 10px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: 0,
};

const iconButtonStyle: CSSProperties = {
  width: 34,
  height: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-secondary)',
  border: 'none',
  borderRadius: 17,
  background: 'var(--hover, rgba(0,0,0,0.04))',
  cursor: 'pointer',
  flexShrink: 0,
};

const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  overflowY: 'auto',
  padding: '12px 22px 20px',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const questionStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0,
};

const answerStyle: CSSProperties = {
  margin: 0,
  color: 'var(--text-secondary)',
  fontSize: 14,
  lineHeight: '20px',
};

const footerStyle: CSSProperties = {
  padding: '2px 22px 18px',
};

const doneButtonStyle: CSSProperties = {
  width: '100%',
  height: 46,
  borderRadius: 9999,
  border: 0,
  backgroundColor: 'var(--text-primary)',
  color: 'var(--bg)',
  fontFamily: 'inherit',
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'opacity 150ms',
};
